// File: src/components/landing/GatheringsSection.jsx
import React, { useContext } from "react";
import EventCard from "../EventCard";
import LocationField from "../LocationField";
import { EventsContext } from "../../layouts/StandardLayout";

export default function GatheringsSection({
  events = [],
  onEventClick,
}) {
  const {
    selectedMarketId = "",
    selectedMarketLabel = "",
    onMarketSelected,
    isLoadingEvents = false,
    eventsError = "",
    refreshEvents,
  } = useContext(EventsContext) || {};

  const marketEvents =
    selectedMarketId && Array.isArray(events)
      ? events.filter((event) => {
          const marketId = event.marketId?._id ?? event.marketId;

          return String(marketId || "") === String(selectedMarketId);
        })
      : [];

  const hasEvents = marketEvents.length > 0;

  return (
    <section
      id="gatherings"
      className="gatheringsSection"
      style={styles.eventsSection}
    >
      <style>
        {`
          .gatheringsSection {
            display: none;
          }

          .gatheringsMarketPicker > div {
            width: auto !important;
            flex: 0 1 auto !important;
            justify-content: flex-start !important;
          }

          @media (max-width: 450px) {
            .gatheringsSection {
              display: block !important;
            }

            .gatheringsTitle {
              margin: 10px 0 0 !important;
              font-size: clamp(2.05rem, 8.6vw, 2.55rem) !important;
              line-height: 1.08 !important;
              letter-spacing: -0.015em !important;
              font-weight: 400 !important;
              text-transform: lowercase !important;
              color: #171717 !important;
            }

            .gatheringsSub {
              max-width: 340px !important;
              margin: 14px 0 0 !important;
              font-size: 0.94rem !important;
              line-height: 1.52 !important;
              color: #555 !important;
              font-weight: 430 !important;
              letter-spacing: -0.012em !important;
            }
          }
        `}
      </style>

      <div style={styles.eventsHeader}>
        <p style={styles.sectionEyebrow}>gatherings</p>

        <h2 className="gatheringsTitle" style={styles.eventsTitle}>
          the kinds of plans
          <br />
          we host.
        </h2>

        <p className="gatheringsSub" style={styles.eventsSub}>
          Our plans are designed to be fun, easy to join, and built
          around meeting new people.
        </p>

        <div
          className="gatheringsMarketPicker"
          style={styles.marketPicker}
        >
          <LocationField
            selectedMarketId={selectedMarketId}
            onMarketSelected={onMarketSelected}
          />
        </div>
      </div>

      {!selectedMarketId ? (
        <div style={styles.emptyState}>
          <h3 style={styles.emptyTitle}>choose your city.</h3>
          <p style={styles.emptyText}>
            Select a city above to explore its gatherings.
          </p>
        </div>
      ) : eventsError ? (
        <div style={styles.emptyState} role="alert">
          <h3 style={styles.emptyTitle}>
            couldn’t load gatherings.
          </h3>
          <p style={styles.emptyText}>{eventsError}</p>
          <button
            type="button"
            style={styles.retryButton}
            onClick={() => refreshEvents?.()}
          >
            Try again
          </button>
        </div>
      ) : isLoadingEvents ? (
        <div style={styles.emptyState} role="status">
          <p style={styles.emptyText}>
            Loading gatherings
            {selectedMarketLabel
              ? ` in ${selectedMarketLabel}`
              : ""}
            …
          </p>
        </div>
      ) : hasEvents ? (
        <div
          key={selectedMarketId}
          style={styles.horizontalEventsWrap}
        >
          <div style={styles.horizontalEventsRail}>
            {marketEvents.slice(0, 8).map((event) => (
              <div
                key={event._id || event.id || event.name}
                style={{
                  ...styles.horizontalEventItem,
                  ...(marketEvents.length === 1
                    ? styles.singleHorizontalEventItem
                    : {}),
                }}
              >
                <EventCard
                  landing
                  hasAccount={false}
                  userId={null}
                  event={{
                    ...event,
                    distanceMiles: null,
                    distanceKm: null,
                    distanceMeters: null,
                  }}
                  live={false}
                  upcoming={true}
                  occupantCount={event.occupancyCount || 0}
                  isUserSaved={false}
                  onSaveToggle={() => {}}
                  onNavigate={() => {}}
                  onSelectEvent={() => onEventClick?.(event)}
                  refreshEventData={() => {}}
                />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div style={styles.emptyState}>
          <h3 style={styles.emptyTitle}>
            more gatherings coming soon.
          </h3>
          <p style={styles.emptyText}>
            There are no upcoming gatherings
            {selectedMarketLabel
              ? ` in ${selectedMarketLabel}`
              : " in this city"}{" "}
            right now. Check back for new plans.
          </p>
        </div>
      )}
    </section>
  );
}

const styles = {
  eventsSection: {
    fontFamily:
      "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxWidth: "1080px",
    margin: "0 auto",
    padding: "80px 22px 80px",
    borderTop: "1px solid rgba(0,0,0,0.08)",
  },
  eventsHeader: {
    marginBottom: "28px",
  },
  sectionEyebrow: {
    margin: 0,
    fontFamily: '"Ugly Handwriting", cursive',
    fontSize: "1.25rem",
    fontWeight: 500,
    lineHeight: 1.15,
    letterSpacing: "normal",
    textTransform: "lowercase",
    color: "#343c54",
  },
  eventsTitle: {
    fontFamily: '"Instrument Serif", Georgia, serif',
    fontStyle: "normal",
    margin: "12px 0 0",
    color: "#171717",
    fontSize: "clamp(2rem, 3.7vw, 2.8rem)",
    lineHeight: 1.08,
    fontWeight: 400,
    letterSpacing: "-0.015em",
    textTransform: "none",
  },
  eventsSub: {
    margin: "16px 0 0",
    maxWidth: 390,
    color: "#555",
    fontSize: "0.98rem",
    lineHeight: 1.52,
    fontWeight: 430,
    letterSpacing: "-0.012em",
  },
  marketPicker: {
    marginTop: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    maxWidth: "100%",
    minWidth: 0,
  },
  horizontalEventsWrap: {
    width: "100vw",
    marginLeft: "calc(50% - 50vw)",
    overflowX: "auto",
    overflowY: "hidden",
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "none",
    padding: "2px 0 0px",
  },
  horizontalEventsRail: {
    display: "flex",
    gap: "15px",
    width: "max-content",
    padding: "0 max(22px, calc((100vw - 1080px) / 2))",
  },
  horizontalEventItem: {
    flex: "0 0 auto",
    width: "min(82vw, 350px)",
  },
  singleHorizontalEventItem: {
    width: "calc(100vw - 44px)",
  },
  emptyState: {
    background: "#ffff",
    border: "1.5px dashed rgba(17,17,17,0.14)",
    borderRadius: "18px",
    padding: "150px 40px",
    textAlign: "center",
    width: 275,
  },
  emptyTitle: {
    fontFamily: '"Instrument Serif", Georgia, serif',
    margin: 0,
    color: "#171717",
    fontSize: "clamp(1.28rem, 4vw, 1.9rem)",
    lineHeight: 1.08,
    letterSpacing: "-0.015em",
    fontWeight: 400,
    textTransform: "lowercase",
  },
  emptyText: {
    maxWidth: "420px",
    margin: "12px auto 0",
    fontSize: "0.92rem",
    lineHeight: 1.5,
    color: "#555",
    fontWeight: 430,
    letterSpacing: "-0.01em",
  },
  retryButton: {
    marginTop: 20,
    border: "1px solid rgba(17,17,17,0.15)",
    background: "#fff",
    color: "#171717",
    borderRadius: "999px",
    padding: "10px 18px",
    minHeight: 44,
    fontFamily: "inherit",
    fontSize: "0.9rem",
    fontWeight: 600,
    cursor: "pointer",
  },
};